import { useMemo } from 'react'
import { useGlobalDashboardData, type DashboardDataSource } from '@hooks/useDashboardData'
import { usePendingApprovals, useRecentReceptionNotes } from '@hooks/useScarlettInbox'
import { useAuthStore } from '@hooks/useAuthStore'
import { useClinicFormat } from '@context/ClinicTimezoneContext'
import { HeroToday } from '@components/dashboard/HeroToday'
import { KpiBand } from '@components/dashboard/KpiBand'
import { TodayTimeline } from '@components/dashboard/TodayTimeline'
import { ScarlettPanel } from '@components/dashboard/ScarlettPanel'
import { ActivityFeed } from '@components/dashboard/ActivityFeed'
import { AttentionBanner } from '@components/dashboard/AttentionBanner'

function greeting(): string {
  const h = new Date().getHours()
  if (h < 12) return 'Buenos días'
  if (h < 19) return 'Buenas tardes'
  return 'Buenas noches'
}

function todayLabel(timezone: string): string {
  const parts = new Intl.DateTimeFormat('es-MX', { timeZone: timezone, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).formatToParts(new Date())
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  const weekday = get('weekday')
  return `${weekday.charAt(0).toUpperCase() + weekday.slice(1)}, ${get('day')} de ${get('month')} de ${get('year')}`
}

const ACTIVE = new Set(['scheduled', 'confirmed', 'checked_in', 'in_progress'])
const HIDDEN = new Set(['cancelled', 'rejected'])

const sameLocalDay = (iso: string, timezone: string) => {
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' })
  return fmt.format(new Date(iso)) === fmt.format(new Date())
}

export function Dashboard() {
  const { timezone } = useClinicFormat()
  const { data, isLoading, isError, refetch } = useGlobalDashboardData(timezone)
  const pendingQ = usePendingApprovals()
  const notesQ = useRecentReceptionNotes(12)
  const user = useAuthStore((s) => s.user)

  const todays = useMemo(
    () => (isError ? [] : (data?.todaysAppointments ?? []).filter((a) => !HIDDEN.has(a.status))),
    [data, isError]
  )
  const completedToday = todays.filter((a) => a.status === 'completed').length
  const remainingToday = todays.filter((a) => ACTIVE.has(a.status)).length
  const now = Date.now()
  const next = useMemo(() => {
    const upcoming = todays
      .filter((a) => ACTIVE.has(a.status) && new Date(a.appointment_date).getTime() + (a.duration_minutes || 30) * 60_000 > now)
      .sort((a, b) => a.appointment_date.localeCompare(b.appointment_date))
    return upcoming[0] ?? data?.nextAppointment ?? null
  }, [todays, data, now])

  const pending = pendingQ.isError ? [] : (pendingQ.data ?? [])
  const notes = notesQ.isError ? [] : (notesQ.data ?? [])
  const notesToday = notes.filter((n) => sameLocalDay(n.created_at, timezone)).length
  const unavailable = new Set(data?.unavailableSources ?? [])
  const appointmentsUnavailable = isError || unavailable.has('appointments')
  const summaryUnavailable = isError || appointmentsUnavailable
    || unavailable.has('patients')
    || unavailable.has('patientStats')
    || pendingQ.isError
    || notesQ.isError
  const unavailableLabels = [
    appointmentsUnavailable && 'citas',
    unavailable.has('patients') && 'pacientes',
    unavailable.has('patientStats') && 'estadísticas de pacientes',
    unavailable.has('invoices') && 'facturación',
    unavailable.has('prescriptions') && 'recetas',
    unavailable.has('labs') && 'laboratorio',
    unavailable.has('safetyEvents') && 'seguridad clínica',
    unavailable.has('consultations') && 'notas clínicas',
  ].filter(Boolean)
  const summaryLoading = isLoading || pendingQ.isLoading || notesQ.isLoading
  const activitySources: DashboardDataSource[] = [
    'appointments',
    'safetyEvents',
    'labs',
    'prescriptions',
  ]
  const activitySourcesUnavailable = isError || activitySources.some((source) => unavailable.has(source))
  const activityItems = isError ? [] : (data?.recentActivity ?? [])

  const attention = !isError && !unavailable.has('safetyEvents') && data && data.criticalSafetyEventsCount > 0
    ? [`${data.criticalSafetyEventsCount} evento${data.criticalSafetyEventsCount !== 1 ? 's' : ''} de seguridad clínica crítico${data.criticalSafetyEventsCount !== 1 ? 's' : ''} requieren atención inmediata.`]
    : []

  const doctorName = user?.last_name ? `Dr. ${user.last_name}` : (user?.first_name ?? 'doctor')

  return (
    <div className="flex min-w-0 flex-col gap-6 motion-safe:animate-fade-in sm:gap-8">
      {isError ? (
        <div role="alert" className="rounded-2xl border border-ml-error/20 bg-ml-error/5 px-5 py-4 text-sm text-ml-on-surface-variant">
          <p>No se pudo cargar el tablero. Las secciones independientes disponibles siguen accesibles.</p>
          <button type="button" onClick={() => { void refetch() }} className="mt-2 font-semibold text-ml-primary hover:underline">Reintentar tablero</button>
        </div>
      ) : unavailableLabels.length > 0 && (
        <div role="alert" className="rounded-2xl border border-ml-error/20 bg-ml-error/5 px-5 py-4 text-sm text-ml-on-surface-variant">
          <p>No se pudieron cargar algunos datos: {unavailableLabels.join(', ')}. Las secciones disponibles siguen actualizadas.</p>
          <button type="button" onClick={() => { void refetch() }} className="mt-2 font-semibold text-ml-primary hover:underline">Reintentar tablero</button>
        </div>
      )}

      {summaryUnavailable ? (
        <div className="rounded-2xl border border-dashed border-ml-outline-variant/30 px-5 py-6 text-sm text-ml-on-surface-variant">
          El resumen no está disponible hasta recuperar sus fuentes.
        </div>
      ) : <><HeroToday
        greeting={greeting()}
        doctorName={doctorName}
        dateLabel={todayLabel(timezone)}
        appointmentsToday={todays.length}
        remainingToday={remainingToday}
        pendingApprovals={pending.length}
        newNotes={notesToday}
        next={next}
        isLoading={summaryLoading}
      />

      <KpiBand
        isLoading={summaryLoading}
        appointmentsToday={todays.length}
        completedToday={completedToday}
        remainingToday={remainingToday}
        pendingApprovals={pending.length}
        newPatientsThisWeek={data?.newPatientsThisWeek ?? 0}
        notesToday={notesToday}
      /></>}

      <AttentionBanner items={attention} />

      <div className="grid min-w-0 grid-cols-1 gap-6 xl:grid-cols-12 sm:gap-8">
        <div className="min-w-0 xl:col-span-7">
          {appointmentsUnavailable ? (
            <div role="alert" className="rounded-2xl border border-ml-error/20 bg-ml-error/5 px-5 py-8 text-sm text-ml-on-surface-variant">
              <p>No se pudo cargar la agenda de hoy.</p>
              <button type="button" onClick={() => { void refetch() }} className="mt-2 font-semibold text-ml-primary hover:underline">Reintentar agenda</button>
            </div>
          ) : <TodayTimeline appointments={todays} isLoading={isLoading} />}
        </div>
        <div className="min-w-0 xl:col-span-5"><ScarlettPanel
          pending={pending}
          notes={notes}
          isLoading={pendingQ.isLoading || notesQ.isLoading}
          pendingLoading={pendingQ.isLoading}
          notesLoading={notesQ.isLoading}
          pendingError={pendingQ.isError}
          notesError={notesQ.isError}
          onRetryPending={() => { void pendingQ.refetch() }}
          onRetryNotes={() => { void notesQ.refetch() }}
        /></div>
      </div>

      {activitySourcesUnavailable && activityItems.length === 0 ? (
        <div role="alert" className="rounded-2xl border border-ml-error/20 bg-ml-error/5 px-5 py-8 text-sm text-ml-on-surface-variant">No se pudo cargar la actividad reciente completa.</div>
      ) : (
        <div className="flex flex-col gap-2">
          {activitySourcesUnavailable && <p role="status" className="text-xs text-ml-on-surface-variant">Actividad parcial: una o más fuentes no están disponibles.</p>}
          <ActivityFeed items={activityItems} isLoading={isLoading} />
        </div>
      )}
    </div>
  )
}
